import type { ComponentType } from 'react';

export interface DocTreeNode {
  name: string;
  path: string;
  children?: DocTreeNode[];
}

const docsModules = import.meta.glob('/src/docs/**/*.jsx');

export const resolveDocModule = (
  path: string
): (() => Promise<{ default: ComponentType }>) | undefined => {
  if (path === 'index') return docsModules['/src/docs/index.jsx'];
  return (
    docsModules[`/src/docs/${path}.jsx`] ??
    docsModules[`/src/docs/${path}/index.jsx`]
  );
};

/**
 * Section hubs (directory index docs, including the docs root) are standalone
 * landing pages: they must never be auto-stacked into the infinite-scroll
 * reading flow beneath another doc.
 */
export const isHubPath = (path: string): boolean =>
  path === 'index' || !!docsModules[`/src/docs/${path}/index.jsx`];

export const getDocsTree = async () => {
  const docs = docsModules;
  const tree: DocTreeNode[] = [];

  for (const path in docs) {
    const parts = path.replace('/src/docs/', '').replace('.jsx', '').split('/');
    let currentLevel = tree;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const existingPath = currentLevel.find((item) => item.name === part);

      if (existingPath) {
        if (!existingPath.children) {
          existingPath.children = [];
        }
        currentLevel = existingPath.children;
      } else {
        const newPath: DocTreeNode = {
          name: part,
          path: parts.slice(0, i + 1).join('/'),
        };
        currentLevel.push(newPath);
        if (i < parts.length - 1) {
          newPath.children = [];
          currentLevel = newPath.children;
        }
      }
    }
  }

  function cleanTree(nodes: DocTreeNode[]) {
    for (const node of nodes) {
      if (node.children) {
        // A directory with an index doc resolves to its own URL; the index
        // file itself is loaded through resolveDocModule's alias handling.
        node.children = node.children.filter(child => child.name !== 'index');
        cleanTree(node.children);
      }
    }
  }

  cleanTree(tree);
  return tree.filter(node => node.name !== 'index');
};
